import { User, UserRole } from "@/src/models/user.model";
import * as DocumentPicker from "expo-document-picker";
import * as XLSX from "xlsx";

const normalizeRole = (role?: string): UserRole =>
  role?.toLowerCase() === "admin" ? UserRole.ADMIN : UserRole.MEMBER;

const normalizeKey = (key: string) => key.toLowerCase().replace(/[\s_]/g, "");

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const importInviteUsersFromExcel = async (
  file?: DocumentPicker.DocumentPickerAsset,
): Promise<User[]> => {
  try {
    let selectedFile = file;

    if (!selectedFile) {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          ".xlsx",
          ".xls",
        ],
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return [];
      }

      selectedFile = result.assets[0];
    }

    const response = await fetch(selectedFile.uri);
    if (!response.ok) {
      throw new Error("Failed to read file");
    }
    const arrayBuffer = await response.arrayBuffer();

    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return [];

    const sheet = workbook.Sheets[sheetName];
    const rawRows = XLSX.utils.sheet_to_json<any>(sheet);

    const rows = rawRows.map((row) => {
      const normalized: Record<string, any> = {};

      Object.keys(row).forEach((key) => {
        normalized[normalizeKey(key)] = row[key];
      });

      return normalized;
    });

    return rows
      .filter((row) => {
        const firstName = String(row.firstname || "").trim();
        const lastName = String(row.lastname || "").trim();
        const email = String(row.email || "").trim();
        return firstName && lastName && email && isValidEmail(email);
      })
      .map(
        (row) =>
          ({
            firstName: String(row.firstname).trim(),
            lastName: String(row.lastname).trim(),
            email: String(row.email).trim(),
            role: normalizeRole(row.role),
          }) as User,
      );
  } catch (error) {
    throw error;
  }
};
